import { useApi } from 'contexts/Api/Api';
import { validateEmail } from 'helpers';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import UserAvatarMenu from '../UserAvatarMenu/UserAvatarMenu';
import {
    Backdrop,
    CenteredColumn,
    CheckboxWrapper,
    CloseButton,
    ContainedButton,
    Controls,
    Form,
    FormFields,
    FormHeader,
    HeaderAuthButton,
    HeaderMenuLink,
    MassiveButton,
    ModalContainer,
    ModalContent,
    ModalDivider,
    OAuthButton,
    OAuthIconWrap,
    Row,
    SectionRule,
    Text,
    TextActionButton,
    TextField,
    Title,
} from './LandingAuthControls.styled';

type AuthMode = 'join' | 'login';

type AuthErrors = {
    email?: string;
    general?: string;
    name?: string;
    password?: string;
};

// Host-aware base for orca-front, mirroring the staging-vs-prod convention in
// contexts/Api/Api.tsx (staging.kromatic.com -> staging, everything else -> prod).
export function resolveForgotPasswordBaseUrl(): string {
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        if (hostname === 'staging.kromatic.com') return 'https://staging.kromatic.com';
        if (hostname === 'kromatic.com' || hostname === 'www.kromatic.com') return 'https://kromatic.com';
    }
    return 'https://kromatic.com';
}

export function buildForgotPasswordUrl(baseUrl: string, email: string): string {
    const url = `${baseUrl}/auth/forgot-password`;
    return email ? `${url}?email=${encodeURIComponent(email)}` : url;
}

function getAuthErrorMessage(error: unknown, fallback: string): string {
    if (error == null || typeof error !== 'object') return fallback;
    const err = error as Record<string, unknown>;
    if (typeof err.message === 'string') return err.message;
    if (typeof err.error === 'string') return err.error;
    if (typeof err.detail === 'string') return err.detail;
    const detail = err.detail;
    if (Array.isArray(detail) && detail[0] != null && typeof (detail[0] as Record<string, unknown>).msg === 'string') {
        return (detail[0] as Record<string, string>).msg;
    }
    return fallback;
}

function GoogleIcon() {
    return (
        <OAuthIconWrap aria-hidden>
            <svg height="20" viewBox="0 0 18 18" width="20">
                <path
                    d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.2 1.12-.84 2.06-1.8 2.7v2.24h2.92c1.72-1.58 2.68-3.92 2.68-6.58z"
                    fill="#4285F4"
                />
                <path
                    d="M9 18c2.44 0 4.5-.8 6-2.22l-2.92-2.24c-.8.54-1.82.86-3.08.86-2.36 0-4.36-1.58-5.08-3.72H.92v2.3A9 9 0 0 0 9 18z"
                    fill="#34A853"
                />
                <path
                    d="M3.92 10.68A5.4 5.4 0 0 1 3.64 9c0-.58.1-1.14.28-1.68V5.02H.92A9 9 0 0 0 0 9c0 1.44.34 2.8.92 3.98l3-2.3z"
                    fill="#FBBC05"
                />
                <path
                    d="M9 3.58c1.34 0 2.54.46 3.48 1.36l2.6-2.6C13.5.9 11.44 0 9 0A9 9 0 0 0 .92 5.02l3 2.3C4.64 5.18 6.64 3.58 9 3.58z"
                    fill="#EA4335"
                />
            </svg>
        </OAuthIconWrap>
    );
}

export default function LandingAuthControls() {
    const [mode, setMode] = useState<AuthMode | null>(null);
    const [rememberMe, setRememberMe] = useState(false);
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [joinData, setJoinData] = useState({
        email: '',
        name: '',
        password: '',
        subscribeToNews: true,
    });
    const [loginErrors, setLoginErrors] = useState<AuthErrors>({});
    const [joinErrors, setJoinErrors] = useState<AuthErrors>({});
    const api = useApi();

    const authBaseUrl = useMemo(
        () => (process.env.NEXT_PUBLIC_AUTH_API_URL || process.env.NEXT_PUBLIC_AUTH_TEST_API_URL || '').replace(/\/+$/, ''),
        []
    );

    const closeDialog = () => {
        setMode(null);
        setLoginErrors({});
        setJoinErrors({});
    };

    const openDialog = (nextMode: AuthMode) => {
        setMode(nextMode);
        setLoginErrors({});
        setJoinErrors({});
    };

    const handleOAuthClick = (nextMode: AuthMode) => {
        setLoginErrors({});
        setJoinErrors({});
        if (!api.authConfigured || !authBaseUrl) {
            const msg = 'OAuth is unavailable right now. Please continue with email/password.';
            if (nextMode === 'join') setJoinErrors({ general: msg });
            else setLoginErrors({ general: msg });
            return;
        }
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${authBaseUrl}/auth/login/google`;
        form.style.display = 'none';
        const addField = (name: string, value: string | null | undefined) => {
            if (value == null) return;
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = String(value);
            form.appendChild(input);
        };
        addField('return_url', `${window.location.pathname}${window.location.search}` || '/');
        addField('subscribe_to_news', nextMode === 'join' && joinData.subscribeToNews ? 'true' : 'false');
        document.body.appendChild(form);
        form.submit();
    };

    const handleLoginSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const next: AuthErrors = {};
        if (!loginData.email) next.email = 'Field is required';
        else if (!validateEmail(loginData.email)) next.email = 'Invalid email';
        if (!loginData.password) next.password = 'Field is required';
        if (Object.keys(next).length) {
            setLoginErrors(next);
            return;
        }
        setLoginErrors({});
        await api
            .login({
                email: loginData.email,
                password: loginData.password,
                rememberMe,
            })
            .then(() => closeDialog())
            .catch(err => setLoginErrors({ general: getAuthErrorMessage(err, 'Unable to log in.') }));
    };

    const handleJoinSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const parsed = joinData.name.trim();
        const next: AuthErrors = {};
        if (!parsed) next.name = 'Field is required';
        else if (parsed.length < 2) next.name = 'Ensure this value has at least 2 characters';
        if (!joinData.email) next.email = 'Field is required';
        else if (!validateEmail(joinData.email)) next.email = 'Invalid email';
        if (!joinData.password) next.password = 'Field is required';
        if (Object.keys(next).length) {
            setJoinErrors(next);
            return;
        }
        setJoinErrors({});
        const [firstName, ...rest] = parsed.split(' ');
        await api
            .join({
                email: joinData.email,
                firstName,
                lastName: rest.join(' ').trim() || undefined,
                password: joinData.password,
                subscribeToNews: joinData.subscribeToNews,
            })
            .then(() => closeDialog())
            .catch(err => setJoinErrors({ general: getAuthErrorMessage(err, 'Unable to create account.') }));
    };

    const handleCloseModal = () => {
        if (!api.loading) closeDialog();
    };

    useEffect(() => {
        if (mode === null) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !api.loading) closeDialog();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [mode, api.loading]);

    return (
        <>
            <Controls>
                {api.user ? (
                    <>
                        <HeaderMenuLink data-testid="landing-menu-mykit" href="/mykit">
                            My Kit
                        </HeaderMenuLink>
                        <UserAvatarMenu />
                    </>
                ) : (
                    <>
                        <HeaderAuthButton
                            $alt
                            aria-label="Log in"
                            data-testid="landing-login-button"
                            disabled={api.loading}
                            onClick={() => openDialog('login')}
                        >
                            Log in
                        </HeaderAuthButton>
                        <HeaderAuthButton
                            aria-label="Join now"
                            data-testid="landing-join-button"
                            disabled={api.loading}
                            onClick={() => openDialog('join')}
                        >
                            Join Now
                        </HeaderAuthButton>
                    </>
                )}
            </Controls>

            {(mode === 'login' || mode === 'join') && (
                <ModalContainer data-testid="auth-modal-container" role="dialog" aria-modal="true" aria-label="Authentication modal">
                    <ModalContent>
                        <CloseButton
                            aria-label="Close auth modal"
                            data-testid="auth-modal-close"
                            disabled={api.loading}
                            onClick={handleCloseModal}
                        >
                            <svg fill="currentColor" height="18" viewBox="0 0 24 24" width="18">
                                <path d="M18.3 5.71a.9959.9959 0 0 0-1.41 0L12 10.59 7.11 5.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 13.41l4.89 4.89c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z" />
                            </svg>
                        </CloseButton>

                        <Form
                            $loading={api.loading}
                            onChange={() => {
                                setLoginErrors(prev => (prev.general ? {} : prev));
                                setJoinErrors(prev => (prev.general ? {} : prev));
                            }}
                            onSubmit={mode === 'login' ? handleLoginSubmit : handleJoinSubmit}
                        >
                            {mode === 'login' && (
                                <>
                                    <FormHeader data-testid="auth-modal-login-title">
                                        <Title>Log in</Title>
                                        <OAuthButton
                                            data-testid="login-oauth-google"
                                            disabled={api.loading}
                                            type="button"
                                            onClick={() => handleOAuthClick('login')}
                                        >
                                            <GoogleIcon />
                                            Log in with Google
                                        </OAuthButton>
                                        <ModalDivider data-testid="auth-modal-divider">or use your account</ModalDivider>
                                    </FormHeader>
                                    <FormFields>
                                        <TextField
                                            data-testid="login-email"
                                            disabled={api.loading}
                                            name="email"
                                            placeholder="Work email"
                                            type="email"
                                            value={loginData.email}
                                            onChange={e => {
                                                setLoginData(prev => ({ ...prev, email: e.target.value }));
                                                setLoginErrors(prev => ({ ...prev, email: undefined, general: undefined }));
                                            }}
                                        />
                                        <TextField
                                            data-testid="login-password"
                                            disabled={api.loading}
                                            name="password"
                                            placeholder="Password"
                                            type="password"
                                            value={loginData.password}
                                            onChange={e => {
                                                setLoginData(prev => ({ ...prev, password: e.target.value }));
                                                setLoginErrors(prev => ({ ...prev, general: undefined, password: undefined }));
                                            }}
                                        />
                                        {(loginErrors.general ?? loginErrors.email ?? loginErrors.password) && (
                                            <Text $error data-testid="login-error">
                                                {loginErrors.general ?? loginErrors.email ?? loginErrors.password}
                                            </Text>
                                        )}
                                    </FormFields>
                                    <Row $gap={20} $marginBottom={36}>
                                        <CheckboxWrapper>
                                            <input
                                                aria-label="Remember me"
                                                checked={rememberMe}
                                                disabled={api.loading}
                                                id="remember_me"
                                                type="checkbox"
                                                onChange={e => setRememberMe(e.target.checked)}
                                            />
                                            <label htmlFor="remember_me">Remember me</label>
                                        </CheckboxWrapper>
                                        <TextActionButton
                                            as="a"
                                            data-testid="login-forgot-password"
                                            href={buildForgotPasswordUrl(resolveForgotPasswordBaseUrl(), loginData.email)}
                                        >
                                            Forgot Password?
                                        </TextActionButton>
                                    </Row>
                                    <CenteredColumn>
                                        <MassiveButton data-testid="login-submit" disabled={api.loading} type="submit">
                                            Log in
                                        </MassiveButton>
                                    </CenteredColumn>
                                    <SectionRule />
                                    <CenteredColumn $gap={16} data-testid="login-not-member-section">
                                        <Text $size={18}>Not a member yet?</Text>
                                        <ContainedButton
                                            data-testid="login-open-join"
                                            disabled={api.loading}
                                            type="button"
                                            onClick={() => openDialog('join')}
                                        >
                                            Join now
                                        </ContainedButton>
                                    </CenteredColumn>
                                </>
                            )}

                            {mode === 'join' && (
                                <>
                                    <FormHeader $marginTopBottom="0 20px" data-testid="auth-modal-join-title">
                                        <Title>Join for free</Title>
                                        <Text $center $size={18}>
                                            Please create an account
                                            <br />
                                            to access Kromatic
                                        </Text>
                                        <Row $gap={8} data-testid="registration-have-account-section">
                                            <Text $size={12}>Already have an account?</Text>
                                            <TextActionButton
                                                data-testid="registration-open-login"
                                                disabled={api.loading}
                                                type="button"
                                                onClick={() => openDialog('login')}
                                            >
                                                Log in
                                            </TextActionButton>
                                        </Row>
                                    </FormHeader>
                                    <Row>
                                        <OAuthButton
                                            data-testid="registration-oauth-google"
                                            disabled={api.loading}
                                            type="button"
                                            onClick={() => handleOAuthClick('join')}
                                        >
                                            <GoogleIcon />
                                            Sign up with Google
                                        </OAuthButton>
                                    </Row>
                                    <ModalDivider $margin="20px 0" data-testid="auth-modal-divider">or use your account</ModalDivider>
                                    <FormFields>
                                        <TextField
                                            data-testid="registration-name-input"
                                            disabled={api.loading}
                                            name="name"
                                            placeholder="Your name"
                                            value={joinData.name}
                                            onChange={e => {
                                                setJoinData(prev => ({ ...prev, name: e.target.value }));
                                                setJoinErrors(prev => ({ ...prev, general: undefined, name: undefined }));
                                            }}
                                        />
                                        <TextField
                                            data-testid="registration-email-input"
                                            disabled={api.loading}
                                            name="email"
                                            placeholder="Work email"
                                            type="email"
                                            value={joinData.email}
                                            onChange={e => {
                                                setJoinData(prev => ({ ...prev, email: e.target.value }));
                                                setJoinErrors(prev => ({ ...prev, email: undefined, general: undefined }));
                                            }}
                                        />
                                        <TextField
                                            data-testid="registration-password-input"
                                            disabled={api.loading}
                                            name="password"
                                            placeholder="Password"
                                            type="password"
                                            value={joinData.password}
                                            onChange={e => {
                                                setJoinData(prev => ({ ...prev, password: e.target.value }));
                                                setJoinErrors(prev => ({ ...prev, general: undefined, password: undefined }));
                                            }}
                                        />
                                        {(joinErrors.general ?? joinErrors.name ?? joinErrors.email ?? joinErrors.password) && (
                                            <Text $error data-testid="registration-error">
                                                {joinErrors.general ?? joinErrors.name ?? joinErrors.email ?? joinErrors.password}
                                            </Text>
                                        )}
                                    </FormFields>
                                    <Row $justify="flex-start" $marginBottom={20}>
                                        <CheckboxWrapper data-testid="registration-newsletter-toggle">
                                            <input
                                                aria-label="Subscribe to Kromatic Newsletters"
                                                checked={joinData.subscribeToNews}
                                                data-testid="registration-newsletter-checkbox"
                                                disabled={api.loading}
                                                id="registration-newsletter"
                                                type="checkbox"
                                                onChange={e => setJoinData(prev => ({ ...prev, subscribeToNews: e.target.checked }))}
                                            />
                                            <label htmlFor="registration-newsletter">Subscribe to Kromatic Newsletters</label>
                                        </CheckboxWrapper>
                                    </Row>
                                    <CenteredColumn>
                                        <MassiveButton data-testid="registration-submit" disabled={api.loading} type="submit">
                                            Join now
                                        </MassiveButton>
                                    </CenteredColumn>
                                </>
                            )}
                        </Form>
                    </ModalContent>
                    <Backdrop
                        aria-label="Close auth modal"
                        data-testid="auth-modal-backdrop"
                        disabled={api.loading}
                        onClick={handleCloseModal}
                    />
                </ModalContainer>
            )}
        </>
    );
}
